import React, { useEffect, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import axios from "../axios-config.js";
import {
  STAGING_AUTH_WALL_TOKEN_KEY,
  canPassStagingAuthWall,
  isStagingAuthWallHost,
  isSuperuserTokenPayload,
} from "../utils/stagingAuthWall.js";

function readStoredAccess() {
  const token = window.localStorage.getItem(STAGING_AUTH_WALL_TOKEN_KEY);

  if (!token) {
    return false;
  }

  try {
    const hasAccess = canPassStagingAuthWall(jwt_decode(token));

    if (!hasAccess) {
      window.localStorage.removeItem(STAGING_AUTH_WALL_TOKEN_KEY);
    }

    return hasAccess;
  } catch {
    window.localStorage.removeItem(STAGING_AUTH_WALL_TOKEN_KEY);
    return false;
  }
}

const StagingAuthWall = ({ children }) => {
  const enabled = isStagingAuthWallHost(window.location.hostname);
  const emailInputRef = useRef(null);
  const [hasAccess, setHasAccess] = useState(() => !enabled || readStoredAccess());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (enabled && !hasAccess && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [enabled, hasAccess]);

  if (!enabled || hasAccess) {
    return children;
  }

  const handleSubmit = () => {
    if (isSubmitting) {
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    axios
      .post("/authenticate", { email, password })
      .then((response) => {
        const token = response.data.token;
        const payload = jwt_decode(token);

        if (!canPassStagingAuthWall(payload)) {
          window.localStorage.removeItem(STAGING_AUTH_WALL_TOKEN_KEY);
          setErrorMessage(
            isSuperuserTokenPayload(payload)
              ? "Unable to verify staging access."
              : "Only Inventum superusers can access staging."
          );
          return;
        }

        window.localStorage.setItem(STAGING_AUTH_WALL_TOKEN_KEY, token);
        setEmail("");
        setPassword("");
        setHasAccess(true);
      })
      .catch((error) => {
        const apiMessage = error.response?.data?.error?.userMessage;
        setErrorMessage(apiMessage || "Unable to verify staging access.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="StagingAuthWall">
      <form className="StagingAuthWallForm" onSubmit={(event) => event.preventDefault()}>
        <div className="StagingAuthWallTitle">Staging Access</div>
        <input
          autoComplete="email"
          ref={emailInputRef}
          onKeyPress={handleKeyPress}
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          placeholder="Email/Username"
          type="email"
        />
        <input
          autoComplete="current-password"
          onKeyPress={handleKeyPress}
          onChange={(event) => setPassword(event.target.value)}
          value={password}
          placeholder="Password"
          type="password"
        />
        {errorMessage ? <div className="StagingAuthWallError">{errorMessage}</div> : null}
        <input
          className="StagingAuthWallSubmit"
          disabled={isSubmitting}
          onClick={handleSubmit}
          type="button"
          value={isSubmitting ? "Checking..." : "Continue"}
        />
      </form>
    </div>
  );
};

export default StagingAuthWall;
